import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { RolePermissionsPanel } from "@/components/permissions/RolePermissionsPanel";
import { UserOverrideDialog } from "@/components/permissions/UserOverrideDialog";

function UsersList({ writable }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null); // { id, label }

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users/"), api.get("/users/roles")]);
    setUsers(u.data.items || []);
    setRoles(r.data.roles || []);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ active: true, role: "Manager" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.id; delete payload.email; delete payload.created_at; delete payload.created_by; delete payload.password_hash;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users/", form);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (u) => {
    if (!confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      {writable && (
        <div className="flex justify-end">
          <Button onClick={openNew} className="kn-gradient-bg text-white border-0" data-testid="create-user-button">
            <Plus className="w-4 h-4 mr-2" /> New User
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u) => {
          const initials = (u.name || u.email).split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();
          return (
            <Card key={u.id} className="kn-card p-5 rounded-xl border border-border bg-card">
              <div className="flex items-start gap-3">
                <Avatar className="w-11 h-11">
                  <AvatarFallback className="kn-gradient-bg text-white text-sm font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{u.name || u.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  {u.supervisor_id && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      Reports to {(() => { const s = users.find((x) => x.id === u.supervisor_id); return s ? s.name || s.email : "—"; })()}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">{u.role}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${u.active ? "border-emerald-500/40 text-emerald-500" : "border-rose-500/40 text-rose-500"}`}>
                      {u.active ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                </div>
              </div>
              {writable && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Switch checked={!!u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} />
                    <span className="text-xs text-muted-foreground">Active</span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon" variant="ghost" title="Override this user's permissions"
                      onClick={() => setOverrideTarget({ id: u.id, label: u.name || u.email })}
                      data-testid={`override-permissions-${u.id}`}
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(u)} data-testid={`delete-user-${u.id}`} className="hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit user" : "New user"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            {!editing && (
              <div className="space-y-1.5 md:col-span-2">
                <Label>Email</Label>
                <Input type="email" value={form.email || ""} onChange={(e) => setForm(s => ({ ...s, email: e.target.value }))} data-testid="user-email-input" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name || ""} onChange={(e) => setForm(s => ({ ...s, name: e.target.value }))} data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role || ""} onValueChange={(v) => setForm(s => ({ ...s, role: v }))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone || ""} onChange={(e) => setForm(s => ({ ...s, phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Department</Label>
              <Input value={form.department || ""} onChange={(e) => setForm(s => ({ ...s, department: e.target.value }))} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Supervisor (reviews their daily work reports)</Label>
              <SearchableSelect
                value={form.supervisor_id || "__none__"}
                onValueChange={(v) => setForm((s) => ({ ...s, supervisor_id: v === "__none__" ? "" : v }))}
                options={[
                  { value: "__none__", label: "No supervisor (doesn't submit daily reports)" },
                  ...users.filter((u) => u.active && u.id !== editing?.id).map((u) => ({ value: u.id, label: `${u.name || u.email} · ${u.role}` })),
                ]}
                placeholder="Choose supervisor"
                testId="user-supervisor-select"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>{editing ? "New password (optional)" : "Password"}</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm(s => ({ ...s, password: e.target.value }))} data-testid="user-password-input" required={!editing} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="kn-gradient-bg text-white border-0" data-testid="save-user-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserOverrideDialog
        userId={overrideTarget?.id}
        userLabel={overrideTarget?.label}
        open={!!overrideTarget}
        onOpenChange={(o) => !o && setOverrideTarget(null)}
      />
    </div>
  );
}

export default function Users() {
  const { user, canViewPage } = useAuth();
  const writable = canWrite(user);
  const canView = canViewPage("users");
  const isSuperAdmin = user?.role === "SuperAdmin";

  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="module-forbidden">
        <Lock className="w-8 h-8 text-muted-foreground mb-3" />
        <p className="font-display text-lg font-medium">You don't have permission to view this page</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.28em] text-amber-500 font-semibold">Access Control</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Users &amp; Permissions</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage workspace users{isSuperAdmin ? ", role-based defaults, and per-user overrides." : "."}
        </p>
      </div>

      {isSuperAdmin ? (
        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles-permissions">Roles &amp; Permissions</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-4">
            <UsersList writable={writable} />
          </TabsContent>
          <TabsContent value="roles" className="mt-4">
            <RolePermissionsPanel />
          </TabsContent>
        </Tabs>
      ) : (
        <UsersList writable={writable} />
      )}
    </div>
  );
}
